import { Inject, Injectable, PLATFORM_ID, InjectionToken, ValueProvider } from '@angular/core';
import {
  AnalyticsService as RdsAnalyticsService,
  InteractionEventGlobal,
  MiscEventGlobal,
  SiteName,
} from '@rogers/analytics';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LogService } from '@rci/log-service';
import { RoutingService } from '@rci/routing-service';
import { Cta } from '@rci/common';

export enum InteractionType {
  TAB_CLICK = 'tab click',
  QUICK_LINK = 'quick link',
  CLICK_THROUGH = 'click through',
  CLICK = 'click',
  EXPAND = 'expand',
  COLLAPSE = 'collapse',
}

export enum MiscellaneousType {
  CLICK = 'click',
  SEARCH = 'search',
}

export interface AnalyticsServiceFactoryConfig {
  siteName: SiteName;
}

export const ANALYTICS_SERVICE_FACTORY_CONFIG = new InjectionToken<AnalyticsServiceFactoryConfig>(
  'AnalyticsServiceFactoryConfig',
);

export class AnalyticsServiceFactory {
  static configure(config?: AnalyticsServiceFactoryConfig): ValueProvider {
    return {
      provide: ANALYTICS_SERVICE_FACTORY_CONFIG,
      useValue: config || {},
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    @Inject(PLATFORM_ID) private platform: Object,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private rdsAnalyticsService: RdsAnalyticsService,
    private router: Router,
    private routingService: RoutingService,
    private logService: LogService,
  ) {}

  bootstrap() {
    if (!isPlatformBrowser(this.platform)) {
      return;
    }
    if (!(window as any)._analyticsHelpers) {
      (window as any)._analyticsHelpers = {};
    }
    (window as any).revealHiddenComponents = (isVisible: boolean) => {
      const body = this.document.getElementsByTagName('body')[0];
      const classNames = body.className.split(' ').filter(c => !!c);
      const unhideClassName = 'adobe-target-unhide';
      const unhideClassIndex = classNames.findIndex(c => c === unhideClassName);
      const isUnhideClassApplied = unhideClassIndex > -1;
      if (isVisible && !isUnhideClassApplied) {
        classNames.push(unhideClassName);
      } else if (!isVisible && isUnhideClassApplied) {
        classNames.splice(unhideClassIndex, 1);
      }
      body.className = classNames.join(' ');
    };
    // hide content that adobe target may replace on route change
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => (window as any).revealHiddenComponents(false));
    // give adobe target at most 5 seconds to load before revealing content,
    // if adobe target loads before 5 seconds, it will call window.revealHiddenComponents
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if ((window as any)._analyticsHelpers.isTargetLoaded) {
        (window as any).revealHiddenComponents(true);
        return;
      }
      setTimeout(() => {
        (window as any).revealHiddenComponents(true);
      }, 5000);
    });
  }

  ctaClick(cta: Cta, componentData, isNavigate, type?: InteractionType) {
    if (cta.isTracked) {
      this.trackClick(cta, componentData, type);
    }
    if (isNavigate && cta.val) {
      this.routingService.navigate(cta.val);
    }
  }

  trackClick(cta: Cta, component, type = InteractionType.CLICK_THROUGH) {
    if (!cta.campaignName) {
      this.trackInteraction(type, `${[cta.context, component.entryTitle].join('|')}`);
      return;
    }

    try {
      this.trackMiscellaneous(
        MiscellaneousType.CLICK,
        `${[component.entryTitle, cta.campaignName].join('|')},${cta.context}`,
      );
    } catch (err) {
      this.logService.error('trackClick>Miscellaneous: ', err);
    }
  }

  trackInteraction(interactionType: InteractionType, interactionName: string) {
    this.rdsAnalyticsService.trackClick(new InteractionEventGlobal(interactionType, interactionName));
  }

  trackMiscellaneous(miscellaneousType: MiscellaneousType, miscellaneousName: string) {
    this.rdsAnalyticsService.trackMisc(new MiscEventGlobal(miscellaneousType, miscellaneousName));
  }
}
